// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Tooltip,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ** Icon Imports
import Icon from "components/icon";
import Frame from "images/Frame.svg";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";

// ** Toast Imports
import 'react-toastify/dist/ReactToastify.css';

// ** Logic Imports
import { useAddAssetLogic } from "../logic/AddAssetLogic";
import { useInventoryPermission } from "../permissions/InventoryPermissions";

const AddAsset = ({ open, handleClose, handleSave, setLoading, selectedAsset,onOpenAssetType, assetTypes, onItemCreated, prefilledAssetTypeID }) => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    hasAssetSavePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  const theme = useTheme();

    const {
      assetForm,
      // assetTypes,
      selectedAssetType,
      errors,
      today,
      handleInputChange,
      handleNumberInputChange,
      handleDateChange,
      // handleCheckboxChange,
      handleStatusChange,
      handleSaveAsset,
      resetForm,
      handleFileChange,
      selectedFile,
      setSelectedFile,
      jobsites,
      handleChangeAssetLocation
    } = useAddAssetLogic({ handleClose, setLoading, open, selectedAsset, prefilledAssetTypeID });

    const isEditMode = Boolean(selectedAsset?.assetID);

  // Reset form when dialog opens
  useEffect(() => {
    // Only reset the form if no asset is selected (i.e. when adding a new asset)
    if (open && !selectedAsset) {
      resetForm();
    }
  }, [open, selectedAsset]);
 

  // Custom save handler to integrate with parent component
  const onSave = async () => {
    if(!hasAssetSavePermission) return;

    const result = await handleSaveAsset();
    
    if (result && result.success) {
      // Call parent handleSave if provided (for existing usage)
      if (handleSave) {
        handleSave();
      }
      
      // Call optional callback if provided (for new auto-select feature)
      if (onItemCreated && result.data) {
        onItemCreated(result.data);
      }
    }
  };
  const dialogTitle = selectedAsset ? "Edit Asset" : "Add New Asset";

  const handleRemoveAttachment = (e) => {
    if(!hasAssetSavePermission) return;

    e.stopPropagation();           
    setSelectedFile(null);        
  };
  const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';
  const str = value.toString();

  // Allow typing "." or partial decimals like "0." or ".5"
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }

  // Remove commas and format integer part
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    parts[0] = parseFloat(parts[0]).toLocaleString('en-US');
  }
  return parts.join('.');
};

 useEffect(() => {
  if (open && !selectedAsset && !assetForm.status) {
    handleStatusChange({
      target: {
        name: 'status',
        value: 'Available'
      }
    });
  }
}, [open, selectedAsset, assetForm.status]);
  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
            {dialogTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12} md={12}>
              <CustomTextField
                fullWidth
                required
                label="Asset Name"
                placeholder="Enter the asset name"
                name="assetName"
                value={assetForm.assetName}
                onChange={handleInputChange}
                error={Boolean(errors.assetName)}
                helperText={errors.assetName}
              />
            </Grid>
            {/* <Grid item xs={12} md={6}>
            <CustomTextField
              fullWidth
              select
              label="Asset Type *"
              name="assetTypeID"
              value={assetForm.assetTypeID || ""}
              onChange={handleInputChange}
              error={Boolean(errors.assetTypeID)}
              helperText={errors.assetTypeID}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {assetTypes.map((type) => (
                <MenuItem key={type.typeCatalogID} value={type.typeCatalogID}>
                  {type.typeName}
                </MenuItem>
              ))}
            </CustomTextField>
            </Grid> */}
            <Grid item xs={12} md={12}>
              <CustomAutocomplete
                fullWidth
                required
                disabled={Boolean(prefilledAssetTypeID)} // Disable if prefilled
                options={[
                  { value: '__ADD_NEW__', label: '+ Create New Asset Type', isAddNew: true },
                  ...assetTypes
                    .map(type => ({ value: type.typeCatalogID, label: type.typeName }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                ]}
                value={
                  assetForm.assetTypeID 
                    ? assetTypes.find(type => type.typeCatalogID === assetForm.assetTypeID) 
                      ? { value: assetForm.assetTypeID, label: assetTypes.find(type => type.typeCatalogID === assetForm.assetTypeID)?.typeName }
                      : null
                    : null
                }
                onChange={(event, newValue) => {
                  if (newValue?.value === '__ADD_NEW__') {
                    onOpenAssetType();
                  } else {
                    handleInputChange({
                      target: {
                        name: 'assetTypeID',
                        value: newValue?.value || ''
                      }
                    });
                  }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Asset Type"
                    placeholder="Select Asset Type"
                    required
                    error={Boolean(errors.assetTypeID)}
                    helperText={errors.assetTypeID}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ 
                      fontWeight: option.value === '__ADD_NEW__' ? 'bold' : 'normal',
                    }}>
                      {option.label}
                    </span>
                  </li>
                )}
                PaperProps={{
                  sx: {
                    maxHeight: 200 
                  }
                }}
              />
            </Grid>
                      {(() => {
              const selectedType = assetTypes.find(
                (type) => type.typeCatalogID === assetForm.assetTypeID
              );
              if (selectedType?.isVehicle) {
                return (
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      fullWidth
                      label="License Plate"
                      placeholder="Enter the License Plate number"
                      name="licensePlate"
                      value={assetForm.licensePlate || ""}
                      onChange={handleInputChange}
                      error={Boolean(errors.licensePlate)}
                      helperText={errors.licensePlate}
                    />
                  </Grid>
                );
              }
              return null;
            })()}

            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Serial/VIN Number"
                placeholder="Enter the Serial or VIN number"
                name="serialVINNumber"
                value={assetForm.serialVINNumber}
                onChange={handleInputChange}
                error={Boolean(errors.serialVINNumber)}
                helperText={errors.serialVINNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Asset Description"
                placeholder="Enter asset description"
                name="description"
                multiline
                rows={3}
                value={assetForm.description}
                onChange={handleInputChange}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
             <CustomTextField
            fullWidth
            // label="Hourly Rate"
            label={
              <Box display="flex" alignItems="center">
                 <Tooltip
                  arrow
                  placement="right"
                  title={
                    <>
                     If you choose to bill the Customer for this Asset, this will be the prefilled rate in your Resources tab. It can always be changed at time of Estimate or Work Order.
                    </>
                  }
                >
                  <IconButton size="small" sx={{ p: 0, mr: 0.5, color: "text.secondary" }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                </Tooltip>
                Hourly Rate
               
              </Box>
            }
            placeholder="$ 0.00"
            name="hourlyRate"
            value={formatNumberWithCommas(assetForm.hourlyRate)}
            onChange={handleNumberInputChange}
          />

            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Designated Operator"
                placeholder="Name of designated operator"
                name="designatedOperator"
                value={assetForm.designatedOperator}
                onChange={handleInputChange}
              />
            </Grid>
          <Grid item xs={12} md={6}>
                  <CustomAutocomplete
                    options={[
                      { value: 'Available', label: 'Available' },
                      { value: 'Dropped at location', label: 'Dropped at location' },
                      { value: 'Unavailable', label: 'Unavailable' }
                    ].sort((a, b) => a.label.localeCompare(b.label))}
                    value={assetForm.status ? { value: assetForm.status, label: assetForm.status } : { value: 'Available', label: 'Available' }}
                    onChange={(event, newValue) => {
                      handleStatusChange({
                        target: {
                          name: 'status',
                          value: newValue?.value || 'Available'
                        }
                      });
                    }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Status"
                        placeholder="Select Status"
                        name="status"
                      />
                    )}
                    PaperProps={{
                      sx: {
                        maxHeight: 200
                      }
                    }}
                  />
                </Grid>
            <Grid item xs={12} md={6}>
              <CustomAutocomplete
                fullWidth
                required
                options={jobsites || []}
                getOptionLabel={(option) => option.jobsiteName || null}
                value={
                  jobsites?.find(
                    (jobsite) =>
                      jobsite.jobsiteID === assetForm?.assetLocation
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleChangeAssetLocation(
                     newValue ? newValue.jobsiteID : null
                  );
                }}
                isOptionEqualToValue={(option, value) =>
                  option.jobsiteID === value.assetLocation
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Asset Location"
                    placeholder="Select Jobsite"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Operational Costs
              </FormLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,

                }}
              >
                <Typography variant="h4" fontWeight={300}>$</Typography>
                <CustomTextField 
                  fullWidth 
                  placeholder="Amount" 
                  name="operationalCostsAmount"
                  value={formatNumberWithCommas(assetForm.operationalCostsAmount)}
                  onChange={handleNumberInputChange}
                />
                <Typography variant="h4">/</Typography>
                <CustomTextField 
                  fullWidth 
                  select 
                  name="operationalCostsUnit"
                  value={assetForm.operationalCostsUnit || "day"}
                  onChange={handleInputChange}
                >
                  <MenuItem value="hour">Hour</MenuItem>
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </CustomTextField>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Next Maintenance Date
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      error: Boolean(errors.nextMaintenanceDate),
                      helperText: errors.nextMaintenanceDate 
                    } 
                  }}
                  value={assetForm.nextMaintenanceDate}
                  onChange={(value) => handleDateChange("nextMaintenanceDate", value)}
                  // disablePast
                  // minDate={today}
                  disablePast={!isEditMode}
                  minDate={!isEditMode ? today : undefined}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Last Maintenance Date
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      error: Boolean(errors.lastMaintenanceDate),
                      helperText: errors.lastMaintenanceDate
                    }
                  }}
                  value={assetForm.lastMaintenanceDate}
                  onChange={(value) => handleDateChange("lastMaintenanceDate", value)}
                  disableFuture
                  maxDate={today}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Purchase Date
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      error: Boolean(errors.purchaseDate),
                      helperText: errors.purchaseDate
                    } 
                  }}
                  value={assetForm.purchaseDate}
                  onChange={(value) => handleDateChange("purchaseDate", value)}
                  disableFuture
                  maxDate={today}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Purchase Price"
                placeholder="$ 0.00"
                name="purchasePrice"
                value={formatNumberWithCommas(assetForm.purchasePrice)}
                onChange={handleNumberInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                  height: "100%",
                }}
              >
                Warranty Expiration
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  slotProps={{ 
                    textField: { 
                      size: "small",
                      error: Boolean(errors.warrantyExpiration),
                      helperText: errors.warrantyExpiration
                    } 
                  }}
                  value={assetForm.warrantyExpiration}
                  onChange={(value) => handleDateChange("warrantyExpiration", value)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  marginBottom: "4px",
                  color: "text.primary",
                }}
              >
                Attachment
              </FormLabel>

              <Box
                onClick={() => document.getElementById("file-upload").click()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 4,
                  py: 2,
                  gap: 2,
                  border: "1px solid rgba(47, 43, 61, 0.2)",
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <input
                  id="file-upload"
                  type="file"
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4"
                  onChange={(e) =>
                    e.target.files?.[0] && handleFileChange(e.target.files[0])
                  }
                />

                {!selectedFile ? (
                  <>
                    <Icon icon="tabler:cloud-upload" />
                    <Typography color="text.secondary">
                      Drop files to attach or click to browse
                    </Typography>
                  </>
                ) : (
                  <>
                  <Tooltip title={selectedFile.name} arrow placement="top">
                    <span>
                      <Icon
                        icon="tabler:paperclip"
                        fontSize="24"
                        style={{ cursor: "pointer" }}
                      />
                    </span>
                  </Tooltip>
                  <IconButton
                      size="small"
                      onClick={handleRemoveAttachment}
                      sx={{ ml: 0.5 }}
                    >
                    <CloseIcon fontSize="small" />
                  </IconButton>                  
                  
                  </>
                  
                )}
              </Box>
            </Grid>
            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox 
                    color="success" 
                    name="gpsTracking"
                    checked={assetForm.gpsTracking}
                    onChange={handleCheckboxChange}
                  />
                }
                label="GPS Tracking"
              />
            </Grid> */}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button variant="contained" color="info" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button variant="contained" color="main" 
            disabled={!hasAssetSavePermission}
            {...(hasAssetSavePermission && {
              onClick: () => onSave()
            })}
            >
               {selectedAsset ? "Save Changes" : "Save Asset"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAsset;
